import Hex from '../engine/hex'
import { Terrain } from '../engine/map'
import { IUnitType } from '../engine/unit'
import races from '../engine/units/races'

export interface ILevelDefinition {
  theme: IThemeDefinition
  isBossLevel: boolean

  map: {
    size: number,
    terrains: Map<Terrain, number>,
  }

  opponents: Map<IUnitType, number>

  partyDeployOrigins: Hex[]

  reward: number
}

export interface IThemeDefinition {
  id: string
  name: string
  description: string
}

export const THEMES: IThemeDefinition[] = [
  {
    id: 'plains',
    name: 'Plains',
    description: 'Open fields, broken roads, and roaming warbands.',
  },
  {
    id: 'forest',
    name: 'Forest',
    description: 'Overgrown battlefields, roots, rivers, and beasts.',
  },
  {
    id: 'desert',
    name: 'Desert',
    description: 'Sand, ruins, scrub, and ambush predators.',
  },
  {
    id: 'barren',
    name: 'Barren',
    description: 'Dead earth, cracked stone, and cursed things.',
  },
  {
    id: 'volcano',
    name: 'Volcano',
    description: 'Ash, basalt, lava channels, and fire-born enemies.',
  },
]

const STARTING_ENEMY_VALUE = 10
const MIN_MAP_SIZE = 5
const MAX_MAP_SIZE = 10
const THEME_LENGTH = 7

interface ITerrainRatio {
  terrain: Terrain
  ratio: number
}

export function isThemeBossLevel(level: number) {
  return (level + 1) % THEME_LENGTH === 0
}

export function themeForLevel(level: number, themeId?: string) {
  const forcedTheme = themeId && THEMES.find(theme => theme.id === themeId)
  if (forcedTheme) {
    return forcedTheme
  }

  const themeIndex = Math.floor(level / THEME_LENGTH) % THEMES.length
  return THEMES[themeIndex]
}

function cellsInMap(size: number) {
  // XXX there might be a better way to calculate this, but yolo
  if (size === 1) {
    return 1
  }

  return cellsInMap(size - 1) + (size - 1) * 6
}

function weightedRandomPick(items: number[]): number {
  const sum = items.reduce((a, b) => a + b, 0)
  const draw = Math.random() * sum

  let acc = 0
  for (const [i, value] of Array.from(items.entries())) {
    acc += value
    if (acc > draw) {
      return i
    }
  }

  throw new Error('Unreachable')
}

// this is greedy and more optimal solutions could be found, but it's not
// a huge deal
function pickOpponents(value: number, bag: IUnitType[] = []): IUnitType[] {
  const monsters = races.monsters.filter(m => m.cost <= value)
  if (monsters.length === 0) {
    // no additional monsters are selectable
    return bag
  }

  // the probability of picking a monster is the inverse of its cost
  const probabilities = monsters.map(m => 1 / m.cost)
  const pickedMonster = monsters[weightedRandomPick(probabilities)]

  return pickOpponents(value - pickedMonster.cost, [...bag, pickedMonster])
}

function terrainRatiosForTheme(theme: IThemeDefinition): ITerrainRatio[] {
  switch (theme.id) {
    case 'forest':
      return [
        { terrain: Terrain.Pit, ratio: .08 },
      ]
    case 'desert':
      return [
        { terrain: Terrain.Pit, ratio: .10 },
      ]
    case 'barren':
      return [
        { terrain: Terrain.Pit, ratio: .16 },
      ]
    case 'volcano':
      return [
        { terrain: Terrain.Pit, ratio: .18 },
      ]
    case 'plains':
    default:
      return [
        { terrain: Terrain.Pit, ratio: .07 },
      ]
  }
}

function terrainForTheme(theme: IThemeDefinition, mapSize: number) {
  const cells = cellsInMap(mapSize)
  return terrainRatiosForTheme(theme).reduce((terrains, item) => {
    terrains.set(item.terrain, Math.floor(cells * item.ratio))
    return terrains
  }, new Map<Terrain, number>())
}

/**
 * each level N is defined by the following equation:
 * - enemy_sum_cost: 10 + N * 10
 * - reward: enemy_sum_cost * Max(20 - N, 1) / 20
 * - map_size: Max(Floor(5 + (N / 3)), 10)
 */
export function generateLevel(
  number: number,
  themeId?: string,
): ILevelDefinition {
  const enemyValue = STARTING_ENEMY_VALUE + number * 10
  const reward = Math.floor(enemyValue * Math.max(20 - number, 1) / 70)
  const mapSize = Math.min(MIN_MAP_SIZE + Math.floor(number / 3), MAX_MAP_SIZE)
  const theme = themeForLevel(number, themeId)

  return {
    theme,
    isBossLevel: isThemeBossLevel(number),

    map: {
      size: mapSize,
      terrains: terrainForTheme(theme, mapSize),
    },

    opponents: pickOpponents(enemyValue).reduce(
      (acc: Map<IUnitType, number>, unit) => {
        const units = acc.get(unit) || 0
        acc.set(unit, units + 1)
        return acc
      },
      new Map(),
    ),

    partyDeployOrigins: [
      new Hex(mapSize, 0),
      new Hex(-mapSize, 0),
    ],

    reward,
  }
}



// WEBPACK FOOTER //
// ./src/content/levels.ts